// ============================================================================
// src/services/licenseService.ts - UPDATED FOR STRAPI V5
// ============================================================================

import apiClient from '@/api/apiClient';
import { StrapiNormalizer } from '@/utils/strapi-normalizer';
import {
  LicenseRequest,  
  BusinessLicense,
  Transaction,
  CreateLicenseRequestData,  
  NegotiationMessage,
  License,
  LicensePurchase
} from '@/types/marketplace';

import {  
  PreLicenseRequest,  
  CreatePreLicenseRequestData,
  
} from '@/types/licenses';
import { StrapiResponse, BaseModel, ID, OperationResult } from '@/types/common';

export type {License, LicenseFilters} from '@/types/licenses';
/**
 * Enhanced License Service - UPDATED FOR STRAPI V5
 * 
 * Key changes:
 * - Support 3 sourceTypes: content, public_catalog, license_option
 * - Use documentId for UPDATE/DELETE operations
 * - Proper populate syntax for Strapi v5
 * - Support LicensePurchase → License flow
 */
class LicenseService {

  // ========================================
  // LICENSE REQUEST OPERATIONS
  // ========================================

  /**
   * Create new license request - UPDATED
   * Now supports 3 source types: content, public_catalog, license_option
   */
  async createLicenseRequest(data: CreateLicenseRequestData): Promise<LicenseRequest> {
    try {
      const requestData: any = {
        sourceType: data.sourceType,
        intendedUse: data.intendedUse,
        notes: data.notes,
        requestDate: new Date().toISOString(),
        licenseRequestStatus: 'pending',
        negotiationHistory: [],
        
        // Extended fields
        ...(data.projectName && { projectName: data.projectName }),
        ...(data.projectType && { projectType: data.projectType }),
        ...(data.budget && { budget: data.budget }),
        ...(data.targetAudience && { targetAudience: data.targetAudience }),
        ...(data.detailedDescription && { detailedDescription: data.detailedDescription }),
        
        // Terms
        ...(data.territory && { territory: data.territory }),
        ...(data.duration && { duration: data.duration }),
        ...(data.channels && { channels: data.channels }),
        ...(data.proposedPrice && { proposedPrice: data.proposedPrice })
      };

      // Set the appropriate relation based on sourceType
      switch (data.sourceType) {
        case 'content':
          requestData.content = data.sourceId;
          break;
        case 'public_catalog':
          requestData.publicCatalog = data.sourceId;
          break;
        case 'license_option':
          requestData.licenseOption = data.sourceId;
          break;
      }

      const result = await apiClient.createRecord<LicenseRequest>('license-requests', requestData);
      return result.data;
    } catch (error) {
      console.error('Failed to create license request:', error);
      throw error;
    }
  }

  /**
   * Update license request - USE documentId
   */
  async updateLicenseRequest(
    requestId: ID,
    data: Partial<CreateLicenseRequestData>
  ): Promise<LicenseRequest> {
    try {
      const updateData: any = {
        ...(data.intendedUse && { intendedUse: data.intendedUse }),
        ...(data.notes && { notes: data.notes }),
        ...(data.proposedPrice && { proposedPrice: data.proposedPrice }),
        ...(data.territory && { territory: data.territory }),
        ...(data.duration && { duration: data.duration }),
        ...(data.channels && { channels: data.channels }),
        ...(data.projectName && { projectName: data.projectName }),
        ...(data.projectType && { projectType: data.projectType }),
        ...(data.budget && { budget: data.budget })
      };

      const result = await apiClient.updateRecord<LicenseRequest>('license-requests', requestId, updateData);
      return result.data;
    } catch (error) {
      console.error('Failed to update license request:', error);
      throw error;
    }
  }

  /**
   * Get license request by ID
   */
  async getLicenseRequest(requestId: ID): Promise<LicenseRequest> {
    try {
      const params: Record<string, string> = {
        'populate[content]': '*',
        'populate[publicCatalog][populate][content]': '*',
        'populate[licenseOption][populate][public_catalog][populate][content]': '*',
        'populate[requester]': '*',
        'populate[owner]': '*'
      };

      const result = await apiClient.fetchRecord<LicenseRequest>('license-requests', requestId, params);
      return result.data;
    } catch (error) {
      console.error('Failed to get license request:', error);
      throw error;
    }
  }

  /**
   * Get user's license requests
   */
  async getUserLicenseRequests(userId: ID): Promise<LicenseRequest[]> {
    try {
      const params: Record<string, string> = {
        'filters[requester][documentId][$eq]': userId.toString(),
        'populate[content][fields][0]': 'title',
        'populate[content][fields][1]': 'contentKind',
        'populate[publicCatalog][populate][content][fields][0]': 'title',
        'populate[licenseOption][populate][public_catalog][populate][content][fields][0]': 'title',
        'populate[owner][fields][0]': 'username',
        'populate[owner][fields][1]': 'email',
        'sort': 'requestDate:desc'
      };

      const result = await apiClient.fetchRecords<LicenseRequest>('license-requests', params);
      return result.data;
    } catch (error) {
      console.error('Failed to get user license requests:', error);
      return [];
    }
  }

  /**
   * Get incoming license requests (for rights owners)
   */
  async getIncomingLicenseRequests(ownerId: ID): Promise<LicenseRequest[]> {
    try {
      const params: Record<string, string> = {
        'filters[owner][documentId][$eq]': ownerId.toString(),
        'populate[content][fields][0]': 'title',
        'populate[publicCatalog][populate][content][fields][0]': 'title',
        'populate[licenseOption][populate][public_catalog][populate][content][fields][0]': 'title',
        'populate[requester]': '*',
        'sort': 'requestDate:desc'
      };

      const result = await apiClient.fetchRecords<LicenseRequest>('license-requests', params);
      return result.data;
    } catch (error) {
      console.error('Failed to get incoming requests:', error);
      return [];
    }
  }

  /**
   * Approve license request - USE documentId
   */
  async approveLicenseRequest(requestId: ID, terms?: any): Promise<LicenseRequest> {
    try {
      const updateData: any = {
        licenseRequestStatus: 'approved',
        approvalDate: new Date().toISOString()
      };

      if (terms) {
        updateData.agreedTerms = terms;
      }

      const result = await apiClient.updateRecord<LicenseRequest>('license-requests', requestId, updateData);
      return result.data;
    } catch (error) {
      console.error('Failed to approve license request:', error);
      throw error;
    }
  }

  /**
   * Reject license request - USE documentId
   */
  async rejectLicenseRequest(requestId: ID, reason: string): Promise<LicenseRequest> {
    try {
      const result = await apiClient.updateRecord<LicenseRequest>('license-requests', requestId, {
        licenseRequestStatus: 'rejected',
        rejectionReason: reason
      });
      return result.data;
    } catch (error) {
      console.error('Failed to reject license request:', error);
      throw error;
    }
  }

  /**
   * Cancel license request - USE documentId
   */
  async cancelLicenseRequest(requestId: ID): Promise<void> {
    try {
      await apiClient.updateRecord<LicenseRequest>('license-requests', requestId, {
        licenseRequestStatus: 'cancelled'
      });
    } catch (error) {
      console.error('Failed to cancel license request:', error);
      throw error;
    }
  }

  /**
   * Add negotiation message
   */
  async addNegotiationMessage(requestId: ID, message: NegotiationMessage): Promise<LicenseRequest> {
    try {
      // Get current request
      const request = await this.getLicenseRequest(requestId);
      const negotiationHistory = request.negotiationHistory || [];
      
      negotiationHistory.push({
        ...message,
        timestamp: new Date().toISOString()
      });

      const result = await apiClient.updateRecord<LicenseRequest>('license-requests', requestId, {
        negotiationHistory
      });

      return result.data;
    } catch (error) {
      console.error('Failed to add negotiation message:', error);
      throw error;
    }
  }

  // ========================================
  // LICENSE OPERATIONS (Active Licenses)
  // ========================================

  /**
   * Get user's active licenses
   */
  async getUserLicenses(userId: ID): Promise<License[]> {
    try {
      const params: Record<string, string> = {
        'filters[licensee][documentId][$eq]': userId.toString(),
        'filters[licenseStatus][$eq]': 'active',
        'populate[content][fields][0]': 'title',
        'populate[content][fields][1]': 'contentKind',
        'populate[purchase][populate][license_option]': '*',
        'sort': 'startDate:desc'
      };

      const result = await apiClient.fetchRecords<License>('licenses', params);
      return result.data;
    } catch (error) {
      console.error('Failed to get user licenses:', error);
      return [];
    }
  }

  /**
   * Get license by ID
   */
  async getLicenseById(licenseId: ID): Promise<License> {
    try {
      const params: Record<string, string> = {
        'populate[content]': '*',
        'populate[licensee]': '*',
        'populate[purchase][populate][license_option][populate][public_catalog]': '*',
        'populate[template]': '*',
        'populate[payments]': '*'
      };

      const result = await apiClient.fetchRecord<License>('licenses', licenseId, params);
      return result.data;
    } catch (error) {
      console.error('Failed to get license:', error);
      throw error;
    }
  }

  /**
   * Track license usage
   */
  async trackLicenseUsage(licenseId: ID, usageData: any): Promise<void> {
    try {
      await apiClient.createRecord('usages', {
        relatedLicense: licenseId,
        period: new Date().toISOString(),
        usageCount: 1,
        isAuthorized: true,
        detectedBy: 'manual',
        ...usageData
      });
    } catch (error) {
      console.error('Failed to track license usage:', error);
      throw error;
    }
  }

  // ========================================
  // BUSINESS LICENSE OPERATIONS
  // ========================================

  /**
   * Get business licenses for a user
   */
  async getBusinessLicenses(userId: ID): Promise<BusinessLicense[]> {
    try {
      const params: Record<string, string> = {
        'filters[$or][0][assignedTo][documentId][$eq]': userId.toString(),
        'filters[$or][1][approver][documentId][$eq]': userId.toString(),
        'populate[license][populate][content]': '*',
        'populate[assignedTo]': '*',
        'populate[approver]': '*'
      };

      const result = await apiClient.fetchRecords<BusinessLicense>('business-licenses', params);
      return result.data;
    } catch (error) {
      console.error('Failed to get business licenses:', error);
      return [];
    }
  }

  // ========================================
  // TRANSACTION OPERATIONS
  // ========================================

  /**
   * Get user transactions
   */
  async getUserTransactions(userId: ID): Promise<Transaction[]> {
    try {
      const params: Record<string, string> = {
        'filters[$or][0][buyer][documentId][$eq]': userId.toString(),
        'filters[$or][1][seller][documentId][$eq]': userId.toString(),
        'populate[purchase][populate][license_option][populate][public_catalog][populate][content]': '*',
        'populate[buyer][fields][0]': 'username',
        'populate[seller][fields][0]': 'username',
        'sort': 'createdAt:desc'
      };

      const result = await apiClient.fetchRecords<Transaction>('transactions', params);
      return result.data;
    } catch (error) {
      console.error('Failed to get transactions:', error);
      return [];
    }
  }

  /**
   * Create transaction for license purchase
   */
  async createTransaction(purchaseId: ID, amount: number, currency: string = 'VND'): Promise<Transaction> {
    try {
      const transactionId = `TXN-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;

      const result = await apiClient.createRecord<Transaction>('transactions', {
        transactionId,
        purchase: purchaseId,
        type: 'license_purchase',
        amount,
        currency,
        status: 'pending',
        createdAt: new Date().toISOString()
      });

      return result.data;
    } catch (error) {
      console.error('Failed to create transaction:', error);
      throw error;
    }
  }

  /**
   * Update transaction status
   */
  async updateTransactionStatus(transactionId: ID, status: 'pending' | 'completed' | 'failed'): Promise<Transaction> {
    try {
      const result = await apiClient.updateRecord<Transaction>('transactions', transactionId, {
        status
      });
      return result.data;
    } catch (error) {
      console.error('Failed to update transaction status:', error);
      throw error;
    }
  }

  // ========================================
  // PRE-LICENSE REQUEST OPERATIONS
  // ========================================

  /**
   * Create pre-license request
   */
  async createPreLicenseRequest(data: CreatePreLicenseRequestData): Promise<PreLicenseRequest> {
    try {
      const result = await apiClient.createRecord<PreLicenseRequest>('pre-license-requests', {
        content: data.contentId,
        requester: data.requesterId,
        intendedUse: data.intendedUse,
        estimatedBudget: data.estimatedBudget,
        timeline: data.timeline,
        additionalNotes: data.additionalNotes,
        preLicenseRequestStatus: 'pending',
        requestDate: new Date().toISOString()
      });

      return result.data;
    } catch (error) {
      console.error('Failed to create pre-license request:', error);
      throw error;
    }
  }

  /**
   * Get pre-license requests
   */
  async getPreLicenseRequests(userId: ID, type: 'sent' | 'received' = 'sent'): Promise<PreLicenseRequest[]> {
    try {
      const filterField = type === 'sent' ? 'requester' : 'owner';
      const params: Record<string, string> = {
        [`filters[${filterField}][documentId][$eq]`]: userId.toString(),
        'populate[content][fields][0]': 'title',
        'populate[content][fields][1]': 'contentKind',
        'populate[requester][fields][0]': 'username',
        'populate[requester][fields][1]': 'email',
        'populate[owner][fields][0]': 'username',
        'populate[owner][fields][1]': 'email',
        'sort': 'requestDate:desc'
      };

      const result = await apiClient.fetchRecords<PreLicenseRequest>('pre-license-requests', params);
      return result.data;
    } catch (error) {
      console.error('Failed to get pre-license requests:', error);
      return [];
    }
  }

  /**
   * Update pre-license request status
   */
  async updatePreLicenseRequestStatus(
    requestId: ID,
    status: 'pending' | 'approved' | 'rejected' | 'converted'
  ): Promise<PreLicenseRequest> {
    try {
      const result = await apiClient.updateRecord<PreLicenseRequest>('pre-license-requests', requestId, {
        preLicenseRequestStatus: status
      });
      return result.data;
    } catch (error) {
      console.error('Failed to update pre-license request status:', error);
      throw error;
    }
  }

  /**
   * Convert pre-license to full license request
   */
  async convertPreLicenseToRequest(preRequestId: ID, additionalData?: any): Promise<LicenseRequest> {
    try {
      // Get pre-license request
      const preRequest = await apiClient.fetchRecord<PreLicenseRequest>('pre-license-requests', preRequestId, {
        'populate[content]': '*'
      });

      // Create full license request
      const licenseRequestData: CreateLicenseRequestData = {
        sourceType: 'content',
        sourceId: preRequest.data.content.documentId,
        intendedUse: preRequest.data.intendedUse,
        notes: preRequest.data.additionalNotes,
        budget: preRequest.data.estimatedBudget,
        ...additionalData
      };

      const licenseRequest = await this.createLicenseRequest(licenseRequestData);

      // Update pre-request status and link
      await apiClient.updateRecord('pre-license-requests', preRequestId, {
        preLicenseRequestStatus: 'converted',
        resultingLicense: licenseRequest.documentId
      });

      return licenseRequest;
    } catch (error) {
      console.error('Failed to convert pre-license request:', error);
      throw error;
    }
  }

  // ========================================
  // PAYMENT OPERATIONS
  // ========================================

  /**
   * Process payment for license request
   */
  async processPayment(licenseRequestId: ID, paymentData: any): Promise<any> {
    try {
      // Create payment record
      const payment = await apiClient.createRecord('payments', {
        paymentId: `PAY-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`,
        amount: paymentData.amount,
        currency: paymentData.currency || 'VND',
        paymentMethod: paymentData.paymentMethod,
        paymentStatus: 'pending',
        payer: paymentData.payerId
      });

      // Here you would integrate with actual payment gateway
      // For now, we'll simulate payment processing

      // Update payment status
      await apiClient.updateRecord('payments', payment.data.documentId, {
        paymentStatus: 'completed'
      });

      // Update license request
      await apiClient.updateRecord('license-requests', licenseRequestId, {
        licenseRequestStatus: 'approved',
        paymentId: payment.data.documentId
      });

      return payment.data;
    } catch (error) {
      console.error('Failed to process payment:', error);
      throw error;
    }
  }

  // ========================================
  // ANALYTICS & REPORTING
  // ========================================

  /**
   * Get license analytics for owner
   */
  async getLicenseAnalytics(ownerId: ID, period: 'week' | 'month' | 'year' = 'month'): Promise<any> {
    try {
      const dateFrom = new Date();
      switch (period) {
        case 'week':
          dateFrom.setDate(dateFrom.getDate() - 7);
          break;
        case 'month':
          dateFrom.setMonth(dateFrom.getMonth() - 1);
          break;
        case 'year':
          dateFrom.setFullYear(dateFrom.getFullYear() - 1);
          break;
      }

      // Get license requests
      const requests = await apiClient.fetchRecords<LicenseRequest>('license-requests', {
        'filters[owner][documentId][$eq]': ownerId.toString(),
        'filters[requestDate][$gte]': dateFrom.toISOString()
      });

      // Calculate metrics
      const total = requests.data.length;
      const approved = requests.data.filter(r => r.licenseRequestStatus === 'approved').length;
      const pending = requests.data.filter(r => r.licenseRequestStatus === 'pending').length;
      const rejected = requests.data.filter(r => r.licenseRequestStatus === 'rejected').length;

      return {
        period,
        total,
        approved,
        pending,
        rejected,
        approvalRate: total > 0 ? (approved / total) * 100 : 0,
        requests: requests.data
      };
    } catch (error) {
      console.error('Failed to get license analytics:', error);
      throw error;
    }
  }

  /**
   * Get revenue report
   */
  async getRevenueReport(ownerId: ID, period: 'week' | 'month' | 'year' = 'month'): Promise<any> {
    try {
      const dateFrom = new Date();
      switch (period) {
        case 'week':
          dateFrom.setDate(dateFrom.getDate() - 7);
          break;
        case 'month':
          dateFrom.setMonth(dateFrom.getMonth() - 1);
          break;
        case 'year':
          dateFrom.setFullYear(dateFrom.getFullYear() - 1);
          break;
      }

      // Get transactions
      const transactions = await apiClient.fetchRecords<Transaction>('transactions', {
        'filters[seller][documentId][$eq]': ownerId.toString(),
        'filters[createdAt][$gte]': dateFrom.toISOString(),
        'filters[status][$eq]': 'completed'
      });

      // Calculate revenue
      const totalRevenue = transactions.data.reduce((sum, t) => sum + t.amount, 0);
      const transactionCount = transactions.data.length;
      const averageTransaction = transactionCount > 0 ? totalRevenue / transactionCount : 0;

      return {
        period,
        totalRevenue,
        transactionCount,
        averageTransaction,
        transactions: transactions.data
      };
    } catch (error) {
      console.error('Failed to get revenue report:', error);
      throw error;
    }
  }

  /**
   * Get license metrics for user
   */
  async getLicenseMetrics(userId: ID, period: 'week' | 'month' | 'year' = 'month'): Promise<any> {
    try {
      const [myRequests, myLicenses, myPurchases] = await Promise.all([
        this.getUserLicenseRequests(userId),
        this.getUserLicenses(userId),
        apiClient.fetchRecords<LicensePurchase>('license-purchases', {
          'filters[buyer][documentId][$eq]': userId.toString()
        })
      ]);

      return {
        period,
        totalRequests: myRequests.length,
        activeLicenses: myLicenses.filter(l => l.licenseStatus === 'active').length,
        totalPurchases: myPurchases.data.length,
        totalSpent: myPurchases.data.reduce((sum, p) => sum + p.totalPrice, 0),
        pendingRequests: myRequests.filter(r => r.licenseRequestStatus === 'pending').length,
        approvedRequests: myRequests.filter(r => r.licenseRequestStatus === 'approved').length,
        rejectedRequests: myRequests.filter(r => r.licenseRequestStatus === 'rejected').length
      };
    } catch (error) {
      console.error('Failed to get license metrics:', error);
      throw error;
    }
  }

  // ========================================
  // BULK OPERATIONS
  // ========================================

  /**
   * Bulk approve license requests
   */
  async bulkApproveLicenseRequests(requestIds: ID[], terms?: any): Promise<LicenseRequest[]> {
    try {
      const promises = requestIds.map(id => this.approveLicenseRequest(id, terms));
      return await Promise.all(promises);
    } catch (error) {
      console.error('Failed to bulk approve requests:', error);
      throw error;
    }
  }

  /**
   * Bulk reject license requests
   */
  async bulkRejectLicenseRequests(requestIds: ID[], reason: string): Promise<LicenseRequest[]> {
    try {
      const promises = requestIds.map(id => this.rejectLicenseRequest(id, reason));
      return await Promise.all(promises);
    } catch (error) {
      console.error('Failed to bulk reject requests:', error);
      throw error;
    }
  }

  // ========================================
  // HELPER METHODS
  // ========================================

  /**
   * Calculate license price based on usage rights
   */
  calculateLicensePrice(basePrice: number, usageRights: any, duration?: number): number {
    let price = basePrice;

    // Apply multipliers based on usage rights
    if (usageRights.canUseCommercially) {
      price *= 2;
    }
    if (usageRights.exclusivity === 'exclusive') {
      price *= 5;
    } else if (usageRights.exclusivity === 'limited_exclusive') {
      price *= 3;
    }
    if (usageRights.canDistribute) {
      price *= 1.5;
    }
    if (usageRights.sublicensing) {
      price *= 2;
    }

    // Apply duration multiplier
    if (duration) {
      const yearFactor = duration / 365;
      price *= yearFactor;
    }

    return Math.round(price);
  }

  /**
   * Validate license request data
   */
  validateLicenseRequest(data: CreateLicenseRequestData): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!data.sourceType || !['content', 'public_catalog', 'license_option'].includes(data.sourceType)) {
      errors.push('Source type không hợp lệ');
    }

    if (!data.sourceId) {
      errors.push('Source ID là bắt buộc');
    }

    if (!data.intendedUse || data.intendedUse.trim().length < 10) {
      errors.push('Mô tả mục đích sử dụng phải có ít nhất 10 ký tự');
    }

    if (data.budget && data.budget < 0) {
      errors.push('Ngân sách không thể âm');
    }

    if (data.proposedPrice && data.proposedPrice < 0) {
      errors.push('Giá đề xuất không thể âm');
    }

    if (data.duration && data.duration < 1) {
      errors.push('Thời hạn phải lớn hơn 0');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Generate license key
   */
  generateLicenseKey(prefix: string = 'LIC'): string {
    const timestamp = Date.now().toString(36).toUpperCase();
    const random = Math.random().toString(36).substr(2, 8).toUpperCase();
    return `${prefix}-${timestamp}-${random}`;
  }

  /**
   * Check if license is expired
   */
  isLicenseExpired(license: License): boolean {
    if (!license.endDate) {
      return false; // Perpetual license
    }
    return new Date(license.endDate) < new Date();
  }

  /**
   * Check if license is expiring soon (within 30 days)
   */
  isLicenseExpiringSoon(license: License, days: number = 30): boolean {
    if (!license.endDate) {
      return false;
    }
    const expiryDate = new Date(license.endDate);
    const warningDate = new Date();
    warningDate.setDate(warningDate.getDate() + days);
    return expiryDate <= warningDate && expiryDate > new Date();
  }
}

export const licenseService = new LicenseService();
export default licenseService;